import React from 'react';
import { styled } from '@linaria/react';
import chroma from 'chroma-js';

import HueBar from './HueBar';
import SatLitGrid from './SatLitGrid';

interface Props {
  color: { hue: number; saturation: number; lightness: number };
  unitSize: number;
  focusInputOnMount?: boolean;
  setColor: (color: {
    hue: number;
    saturation: number;
    lightness: number;
  }) => void;
}

const CompactColorPicker = ({
  color,
  unitSize,
  focusInputOnMount,
  setColor,
}: Props) => {
  const cachedColor = React.useRef(color);
  React.useEffect(() => {
    cachedColor.current = color;
  }, [color]);

  const colorHex = convertToHex(color);

  // I thought for a second that I could derive the hex value for the text input, but I can't because it's possible for users to type in invalid hex codes, and we still want to reflect that in the input. Also, we can't leave it as an uncontrolled input because updating the SatLitGrid and HueBar should update the text input.
  const [textInputValue, setTextInputValue] =
    React.useState(colorHex);

  const textInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    if (focusInputOnMount) {
      textInputRef.current?.focus({
        preventScroll: true,
      });
      textInputRef.current?.select();
    }
    // Intentionally omitting `focusInputOnMount`, since this prop should never change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTextInputChange = React.useCallback(
    function handleTextInputChange(
      ev: React.ChangeEvent<HTMLInputElement>
    ) {
      const { value } = ev.target;

      // No matter what, update the textInputValue state
      setTextInputValue(value.toUpperCase());

      // We don't want to update the *actual* color unless it's valid
      // though, to prevent everything from breaking while the user
      // is halfway through typing a hex code.

      const isValid = chroma.valid(value);

      if (!isValid) {
        // TODO: Should I have some sort of visual sign that it's
        // not valid?
        return;
      }

      setColor(convertToHSL(value));
    },
    [setColor]
  );

  // When the user drags the colors around, update the text input
  const handleHSLValueChange = React.useCallback(
    function handleHSLValueChange(newProps: {
      hue?: number;
      saturation?: number;
      lightness?: number;
    }) {
      const hue = newProps.hue ?? cachedColor.current.hue;
      const saturation =
        newProps.saturation ?? cachedColor.current.saturation;
      const lightness =
        newProps.lightness ?? cachedColor.current.lightness;

      setTextInputValue(convertToHex({ hue, saturation, lightness }));

      setColor({ hue, saturation, lightness });
    },
    [setColor]
  );

  return (
    <Wrapper
      style={{
        '--unit-size': `${unitSize}px`,
      }}
    >
      <Row>
        <TextInput
          ref={textInputRef}
          placeholder="#FF0000"
          value={textInputValue}
          onChange={handleTextInputChange}
          style={{
            '--fade-delay': '100ms',
          }}
        />
        <SatLitGrid
          size={unitSize}
          hue={color.hue}
          saturation={color.saturation}
          lightness={color.lightness}
          fadeDelay={150}
          onChange={handleHSLValueChange}
        />
        <HueBar
          hue={color.hue}
          saturation={color.saturation}
          lightness={color.lightness}
          fadeDelay={260}
          onChange={handleHSLValueChange}
        />
      </Row>
    </Wrapper>
  );
};

function convertToHex({
  hue: h,
  saturation: s,
  lightness: l,
}: {
  hue: number;
  saturation: number;
  lightness: number;
}) {
  return chroma(h, s, l, 'hsl').hex().toUpperCase();
}
function convertToHSL(hex: string) {
  const color = chroma(hex);

  return {
    hue: color.get('hsl.h'),
    saturation: color.get('hsl.s'),
    lightness: color.get('hsl.l'),
  };
}

const Wrapper = styled.div`
  --gap: 32px;
  --white-border-thickness: 2px;
  width: var(--unit-size);
  color-scheme: light;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TextInput = styled.input`
  display: block;
  padding: 8px;
  font-size: 1.25rem;
  font-family: var(--font-family-mono);
  text-align: center;
  border: 2px solid white;
  border-radius: 4px;
  outline-offset: 4px;
  box-shadow: var(--shadow-elevation-medium);
  animation: fadeIn 250ms both;
  animation-delay: var(--fade-delay);
`;

export default CompactColorPicker;
